import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import Modal from "../Common/Modal";
import { toast } from "sonner"
import Button from "../Common/Button"
import { useTranslation } from '../../utils/translations';
import { useAuth } from '../../context/AuthContext';

const { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiLoader, FiUpload, FiExternalLink, FiImage } = FiIcons;

const GuidesSection = ({ isAdmin }) => {
  const { user } = useAuth();
  const t = useTranslation(user?.language);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [currentGuide, setCurrentGuide] = useState({ title: '', description: '', external_url: '', image_url: '' });
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const fileInputRef = useRef(null);
  
  // Fetch guides
  useEffect(() => {
    const fetchGuides = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('help_guides_a1b2c3d4e5')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setGuides(data || []);
      } catch (err) {
        console.error('Error fetching guides:', err);
        toast.error(t('helpCenter.guidesSection.failedToLoad', 'Failed to load guides. Please try again later.'));
      } finally {
        setLoading(false);
      }
    };

    fetchGuides();
  }, []);

  // Handle file change
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/i)) {
      toast.error(t('helpCenter.guidesSection.invalidImageFile', 'Please select a valid image file (PNG, JPG, GIF, or WebP)'));
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('helpCenter.guidesSection.imageSizeTooLarge', 'Image must be less than 5MB'));
      return;
    }
    
    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload image to Supabase Storage
  const uploadImage = async () => {
    if (!imageFile) return null;
    
    try {
      setUploadProgress(0);
      
      // Generate a unique filename
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `guides/${fileName}`;
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('help_center_images')
        .upload(filePath, imageFile, {
          cacheControl: '3600',
          upsert: true,
          onUploadProgress: (progress) => {
            const percent = (progress.loaded / progress.total) * 100;
            setUploadProgress(Math.round(percent));
          }
        });
        
      if (error) throw error;
      
      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('help_center_images')
        .getPublicUrl(filePath);
        
      if (!publicUrlData || !publicUrlData.publicUrl) {
        throw new Error(t('helpCenter.guidesSection.imageUrlError', 'Failed to get public URL for uploaded image'));
      }
      
      return publicUrlData.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  // Handle edit
  const handleEdit = (guide) => {
    setCurrentGuide(guide);
    setIsEditing(true);
    setIsCreating(false);
    setImagePreview(guide.image_url || '');
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm(t('helpCenter.guidesSection.deleteConfirm', 'Are you sure you want to delete this guide?'))) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('help_guides_a1b2c3d4e5')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      // Update local state
      setGuides(guides.filter(guide => guide.id !== id));
      toast.success(t('helpCenter.guidesSection.deleteSuccess', 'Guide deleted successfully.'));
    } catch (err) {
      console.error('Error deleting guide:', err);
      toast.error(t('helpCenter.guidesSection.deleteFailed', 'Failed to delete guide. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Handle save/create
  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!currentGuide.title.trim()) {
      toast.error(t('helpCenter.guidesSection.titleRequired', 'Title is required.'));
      return;
    }

    setSaving(true);
    try {
      // Upload image if selected
      let imageUrl = currentGuide.image_url;
      if (imageFile) {
        imageUrl = await uploadImage();
      }
      
      if (isEditing) {
        // Update existing guide
        const { error } = await supabase
          .from('help_guides_a1b2c3d4e5')
          .update({
            title: currentGuide.title,
            description: currentGuide.description,
            external_url: currentGuide.external_url,
            image_url: imageUrl,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentGuide.id);

        if (error) throw error;
        
        // Update local state
        setGuides(guides.map(guide => 
          guide.id === currentGuide.id ? 
            { ...guide, ...currentGuide, image_url: imageUrl, updated_at: new Date().toISOString() } : 
            guide
        ));
        
        toast.success(t('helpCenter.guidesSection.updateSuccess', 'Guide updated successfully.'));
      } else {
        // Create new guide
        const { data, error } = await supabase
          .from('help_guides_a1b2c3d4e5')
          .insert([{
            title: currentGuide.title,
            description: currentGuide.description,
            external_url: currentGuide.external_url,
            image_url: imageUrl
          }])
          .select();

        if (error) throw error;
        
        // Update local state with the new guide
        if (data && data.length > 0) {
          setGuides([data[0], ...guides]);
        }

        toast.success(t('helpCenter.guidesSection.createSuccess', 'Guide created successfully.'));
      }

      // Reset form and state
      setCurrentGuide({ title: '', description: '', external_url: '', image_url: '' });
      setIsEditing(false);
      setIsCreating(false);
      setImageFile(null);
      setImagePreview('');
      setUploadProgress(0);
    } catch (err) {
      console.error('Error saving guide:', err);
      toast.error(t('helpCenter.guidesSection.saveFailed', 'Failed to save guide. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing/creating
  const handleCancel = () => {
    setCurrentGuide({ title: '', description: '', external_url: '', image_url: '' });
    setIsEditing(false);
    setIsCreating(false);
    setImageFile(null);
    setImagePreview('');
    setUploadProgress(0);
  };

  if (loading && guides.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('helpCenter.guidesSection.title', 'Guides & SOPs')}</h2>
        
        {isAdmin && (
          <Button
            onClick={() => {
              setIsCreating(true);
              setCurrentGuide({ title: '', description: '', external_url: '', image_url: '' });
            }}
            icon={FiPlus}
            responsive
          >
            {t('helpCenter.guidesSection.addGuide', 'Add Guide')}
          </Button>
        )}
      </div>

      {/* Edit/Create Form */}
      <Modal isOpen={isEditing || isCreating} title={isEditing ? t('helpCenter.guidesSection.editGuideTitle', 'Edit Guide') : t('helpCenter.guidesSection.addGuideTitle', 'Add New Guide')} onClose={handleCancel}>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label htmlFor="guide-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('helpCenter.guidesSection.titleLabel', 'Title *')}
            </label>
            <input
              id="guide-title"
              type="text"
              value={currentGuide.title}
              onChange={(e) => setCurrentGuide({ ...currentGuide, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={t('helpCenter.guidesSection.titlePlaceholder', 'Enter the guide title')}
              required
            />
          </div>

          <div>
            <label htmlFor="guide-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('helpCenter.guidesSection.descriptionLabel', 'Description')}
            </label>
            <textarea
              id="guide-description"
              value={currentGuide.description}
              onChange={(e) => setCurrentGuide({ ...currentGuide, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={t('helpCenter.guidesSection.descriptionPlaceholder', 'Enter a brief description')}
              rows={3}
            />
          </div>

          <div>
            <label htmlFor="guide-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('helpCenter.guidesSection.externalLinkLabel', 'External Link')}
            </label>
            <input
              id="guide-url"
              type="url"
              value={currentGuide.external_url}
              onChange={(e) => setCurrentGuide({ ...currentGuide, external_url: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={t('helpCenter.guidesSection.urlPlaceholder', 'https://example.com/guide')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('helpCenter.guidesSection.coverImageLabel', 'Cover Image')}
            </label>
            <div className="flex items-start space-x-4">
              {/* Image Preview */}
              <div className="w-32 h-32 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                {imagePreview ? (
                  <img 
                    src={imagePreview} 
                    alt={t('helpCenter.guidesSection.coverPreviewAlt', 'Cover preview')} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      setImagePreview('');
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <SafeIcon icon={FiImage} className="w-8 h-8 text-gray-400" />
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="flex-1 space-y-2">
                <input 
                  type="file" 
                  ref={fileInputRef}
                  className="hidden" 
                  accept="image/*" 
                  onChange={handleFileChange}
                />
                
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  appendClassName="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                  icon={FiUpload}
                >
                  {t('helpCenter.guidesSection.uploadImage', 'Upload Image')}
                </Button>
                
                {imageFile && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {imageFile.name} ({(imageFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
                
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mt-2">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('helpCenter.guidesSection.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              loading={saving}
              icon={FiSave}
            >
              {isEditing ? t('helpCenter.guidesSection.updateGuide', 'Update Guide') : t('helpCenter.guidesSection.saveGuide', 'Save Guide')}
            </Button>
          </div>
        </form>
      </Modal>
      
      {/* Guides Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {guides.length === 0 ? (
          <div className="col-span-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              {t('helpCenter.guidesSection.noGuides', 'No guides found.')} {isAdmin ? t('helpCenter.guidesSection.addGuidePrompt', 'Click the "Add Guide" button to create one.') : ''}
            </p>
          </div>
        ) : (
          guides.map((guide) => (
            <motion.div
              key={guide.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col"
            >
              {/* Cover Image */}
              <div className="h-40 bg-gray-100 dark:bg-gray-700 overflow-hidden">
                {guide.image_url ? (
                  <img 
                    src={guide.image_url} 
                    alt={guide.title} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x200?text=Image+Not+Available';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {guide.title}
                </h3>
                
                {guide.description && (
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 flex-1">
                    {guide.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between mt-auto pt-4">
                  {guide.external_url ? (
                    <a 
                      href={guide.external_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-blue-600 dark:text-blue-400 text-sm hover:underline"
                    >
                      <span>{t('helpCenter.guidesSection.viewGuide', 'View Guide')}</span>
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                    </a>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 text-sm">{t('helpCenter.guidesSection.noLinkAvailable', 'No link available')}</span>
                  )}
                  
                  {isAdmin && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(guide)}
                        className="p-1.5 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400"
                      >
                        <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(guide.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default GuidesSection;